import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import aiVoiceIcon from 'assets/images/aivoicesearch.svg';
import endButtonIcon from 'assets/images/Endbuttonicon.svg';
import {
  VOICE_UNSUPPORTED_BROWSER_TOOLTIP,
  VOICE_WAVEFORM_MAX_HEIGHT,
  VOICE_WAVEFORM_MIN_HEIGHT,
  clampVoiceAmplitude,
  getInitialVoiceWaveformSamples,
} from 'utils/Voice/VoiceConversationController';

import css from './style.scss';

// Voice buttons for a chat screen, driven by useVoiceConversation (ATAI-324).
// Same controls, icons, copy and states as AI Search's voice mode; each screen
// decides where to place them. AI Search keeps its own copy of this markup in
// Search/Header because there the buttons are interleaved with the search
// bar's other controls (ATAI-323 PR, design §4 fallback).

// True while any part of voice mode is showing (listening, speaking, reading
// the reply aloud). The screen uses it to swap its text controls for these.
export const isVoiceActive = (voice) =>
  Boolean(voice.voiceModeActive || voice.voiceListening || voice.voiceSpeaking || voice.voiceReplyPlaying);

// Status line shown in place of the text box's placeholder in voice mode.
export const getVoiceStatus = (voice, busy) => {
  if (voice.voiceReplyPlaying || (busy && voice.voiceModeActive)) return 'AI is responding...';
  if (voice.micMuted) return 'Microphone muted';
  if (voice.voiceListening || voice.voiceModeActive) return 'Listening...';
  return '';
};

const MicIcon = ({ muted }) => (
  <svg className={css.icon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M8 10.4c1.5 0 2.7-1.2 2.7-2.7V4c0-1.5-1.2-2.7-2.7-2.7S5.3 2.5 5.3 4v3.7c0 1.5 1.2 2.7 2.7 2.7z"
      fill="currentColor"
    />
    <path
      d="M12.7 7.2c.4 0 .7.3.7.7 0 2.5-1.8 4.5-4.1 4.9v1.2h1.6c.4 0 .7.3.7.7s-.3.7-.7.7H5.1c-.4 0-.7-.3-.7-.7s.3-.7.7-.7h1.6v-1.2c-2.3-.4-4.1-2.4-4.1-4.9 0-.4.3-.7.7-.7s.7.3.7.7c0 2.2 1.8 3.6 4 3.6s4-1.4 4-3.6c0-.4.3-.7.7-.7z"
      fill="currentColor"
    />
    {muted && (
      <path d="M2.3 13.7 13.7 2.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    )}
  </svg>
);

const SoundIcon = ({ muted }) => (
  <svg className={css.icon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M2.2 6.1v3.8h2.5l3.4 2.8c.4.3 1 .1 1-.4V3.7c0-.5-.6-.7-1-.4L4.7 6.1H2.2z"
      fill="currentColor"
    />
    {!muted && (
      <>
        <path
          d="M10.8 5.4c.8.7 1.2 1.6 1.2 2.6s-.4 1.9-1.2 2.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <path
          d="M12.4 3.8c1.2 1.1 1.9 2.6 1.9 4.2s-.7 3.1-1.9 4.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </>
    )}
    {muted && (
      <path d="m11.1 6.1 3 3m0-3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    )}
  </svg>
);

// Live waveform while the user is speaking (48 bars, reactive when the mic
// level is available, a gentle pulse otherwise).
export function VoiceWaveform({ samples, reactive }) {
  const waveformSamples = samples || getInitialVoiceWaveformSamples();

  return (
    <span className={classNames(css.waveform, { [css.waveformReactive]: reactive })} aria-hidden="true">
      {waveformSamples.map((height, idx) => {
        const safeHeight = Math.max(
          VOICE_WAVEFORM_MIN_HEIGHT,
          Math.min(VOICE_WAVEFORM_MAX_HEIGHT, height || VOICE_WAVEFORM_MIN_HEIGHT)
        );
        const level = clampVoiceAmplitude(
          (safeHeight - VOICE_WAVEFORM_MIN_HEIGHT) /
            (VOICE_WAVEFORM_MAX_HEIGHT - VOICE_WAVEFORM_MIN_HEIGHT)
        );
        const style = {
          '--voice-waveform-height': `${safeHeight}px`,
          '--voice-waveform-opacity': `${(0.5 + level * 0.5).toFixed(2)}`,
          '--voice-waveform-scale': `${(0.8 + level * 0.4).toFixed(2)}`,
        };

        if (!reactive) style.animationDelay = `${(idx % 10) * 0.05}s`;

        return <span key={idx} style={style} />;
      })}
    </span>
  );
}

VoiceWaveform.propTypes = {
  samples: PropTypes.arrayOf(PropTypes.number),
  reactive: PropTypes.bool,
};

VoiceWaveform.defaultProps = {
  samples: null,
  reactive: false,
};

// What the text box area shows in voice mode: the waveform while the user is
// speaking, otherwise the status line.
export function VoiceStatus({ voice, busy, className }) {
  const status = getVoiceStatus(voice, busy);
  if (!status) return null;

  return (
    <div className={classNames(css.status, className)} aria-live="polite">
      {voice.voiceSpeaking ? (
        <VoiceWaveform samples={voice.voiceWaveformSamples} reactive={voice.voiceAudioReactive} />
      ) : (
        <span>{status}</span>
      )}
    </div>
  );
}

VoiceStatus.propTypes = {
  voice: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  className: PropTypes.string,
};

VoiceStatus.defaultProps = {
  busy: false,
  className: null,
};

// Mic button (voice off) or mic mute + sound mute + End (voice on).
export default function VoiceControls({ voice, busy, startLabel, onEnd, className }) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const hideTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);

  const {
    voiceRecognitionSupported,
    voiceOutputSupported,
    voiceListening,
    micMuted,
    voiceOutputMuted,
    voiceReplyPlaying,
  } = voice;
  const active = isVoiceActive(voice);
  const startDisabled = !voiceRecognitionSupported || micMuted || busy || voiceReplyPlaying;
  const startTitle = !voiceRecognitionSupported
    ? VOICE_UNSUPPORTED_BROWSER_TOOLTIP
    : busy || voiceReplyPlaying
      ? 'Please wait for the current response to finish'
      : micMuted
        ? 'Microphone is muted'
        : voiceListening
          ? 'Stop listening'
          : startLabel;
  const micMuteTitle = micMuted ? 'Unmute microphone' : 'Mute microphone';
  const outputTitle = !voiceOutputSupported
    ? 'AI voice output is not supported in this browser'
    : voiceOutputMuted
      ? 'Unmute AI voice output'
      : 'Mute AI voice output';

  // The designed "use Chrome or Edge" bubble only exists when voice is off
  // because the browser can't do it; everything else uses the native title.
  const openTooltip = () => {
    if (voiceRecognitionSupported) return;
    clearTimeout(hideTimerRef.current);
    setTooltipOpen(true);
  };
  const closeTooltip = () => {
    if (voiceRecognitionSupported) return;
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setTooltipOpen(false), 120);
  };

  if (!active) {
    return (
      <span
        className={classNames(css.controls, className)}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocus={openTooltip}
        onBlur={closeTooltip}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setTooltipOpen(false);
        }}
      >
        <span
          className={css.buttonWrapper}
          title={voiceRecognitionSupported ? startTitle : undefined}
          aria-disabled={startDisabled}
          tabIndex={startDisabled ? 0 : undefined}
        >
          <button
            type="button"
            name="voice-start"
            className={classNames(css.startButton, {
              [css.unsupported]: !voiceRecognitionSupported,
            })}
            disabled={voiceRecognitionSupported && startDisabled}
            aria-label={startTitle}
            aria-pressed={voiceListening}
            onClick={voice.toggleListening}
          >
            <img src={aiVoiceIcon} alt="" aria-hidden="true" />
          </button>
        </span>
        {tooltipOpen && !voiceRecognitionSupported && (
          <span className={css.tooltip} role="note">
            <span>{VOICE_UNSUPPORTED_BROWSER_TOOLTIP}</span>
            <button
              type="button"
              className={css.tooltipClose}
              aria-label="Dismiss voice browser tooltip"
              onClick={() => setTooltipOpen(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={classNames(css.controls, className)}>
      <span className={css.buttonWrapper} title={micMuteTitle}>
        <button
          type="button"
          name="voice-mic-mute"
          className={classNames(css.control, {
            [css.micActive]: voiceListening && !micMuted,
            [css.muted]: micMuted,
          })}
          aria-label={micMuteTitle}
          aria-pressed={micMuted}
          onClick={voice.toggleMicMute}
        >
          <MicIcon muted={micMuted} />
        </button>
      </span>
      <span className={css.buttonWrapper} title={outputTitle}>
        <button
          type="button"
          name="voice-output-mute"
          className={classNames(css.control, { [css.muted]: voiceOutputMuted })}
          disabled={!voiceOutputSupported}
          aria-label={outputTitle}
          aria-pressed={voiceOutputMuted}
          onClick={voice.toggleOutputMute}
        >
          <SoundIcon muted={voiceOutputMuted} />
        </button>
      </span>
      <button type="button" name="voice-end" className={css.endButton} onClick={onEnd || voice.end}>
        <img className={css.endIcon} src={endButtonIcon} alt="" aria-hidden="true" />
        End
      </button>
    </span>
  );
}

VoiceControls.propTypes = {
  voice: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  startLabel: PropTypes.string,
  // Called instead of voice.end() so the screen can also drop a pending
  // spoken reply; it must still call voice.end().
  onEnd: PropTypes.func,
  className: PropTypes.string,
};

VoiceControls.defaultProps = {
  busy: false,
  startLabel: 'Start voice chat',
  onEnd: null,
  className: null,
};
